import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { MagneticButton, Particles, CustomCursor } from './PremiumInteractions';

const rotatingTexts = [
  "Luxury Fleet Management",
  "Chauffeur Excellence",
  "Premium Vehicle Care",
  "Elite Fleet Analytics",
  "Concierge Services",
  "Luxury Asset Protection",
  "VIP Transportation"
];

// Luxury car images for the background slideshow
const backgroundImages = [
  "https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?auto=format&fit=crop&w=2560", // Rolls Royce
  "https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=2560", // Bentley
  "https://images.unsplash.com/photo-1614200187524-dc4b892acf16?auto=format&fit=crop&w=2560", // Aston Martin
  "https://images.unsplash.com/photo-1626668893632-6f3a4466d22f?auto=format&fit=crop&w=2560"  // Luxury Interior
];

const SHOWCASE_VIDEO_URL = import.meta.env.VITE_SHOWCASE_VIDEO_URL || 'https://www.youtube.com/embed/fSyucbksqxw';
const HERO_BACKGROUND_TYPE = import.meta.env.VITE_HERO_BACKGROUND_TYPE || 'carousel';

export default function Hero() {
  const [textIndex, setTextIndex] = useState(0);
  const [bgIndex, setBgIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);

  useEffect(() => {
    const textInterval = setInterval(() => {
      setTextIndex((current) => (current + 1) % rotatingTexts.length);
    }, 2500);

    let bgInterval: NodeJS.Timeout | null = null;
    if (HERO_BACKGROUND_TYPE === 'carousel') {
      bgInterval = setInterval(() => {
        setBgIndex((current) => (current + 1) % backgroundImages.length);
      }, 5000);
    }

    return () => {
      clearInterval(textInterval);
      if (bgInterval) clearInterval(bgInterval);
    };
  }, []);

  const handleEarlyAccess = () => {
    document.querySelector('#newsletter')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <CustomCursor />
      <div className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Background Content */}
        {HERO_BACKGROUND_TYPE === 'carousel' ? (
          // Carousel Background
          <>
            {backgroundImages.map((image, index) => (
              <motion.div
                key={index}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: index === bgIndex ? 0.7 : 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              >
                <img
                  src={image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </>
        ) : (
          // Video Background
          <div className="absolute inset-0 opacity-95">
            <iframe
              src={`${SHOWCASE_VIDEO_URL}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&showinfo=0&rel=0&playlist=${SHOWCASE_VIDEO_URL.split('/').pop()}`}
              className="w-full h-full pointer-events-none"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{ border: 'none' }}
            />
          </div>
        )}

        {/* Overlay Pattern */}
        <div 
          className="absolute inset-0 bg-black/50"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60" />
        
        {/* Hero Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4 max-w-6xl mx-auto"
        >
          <h1 className="text-3xl md:text-5xl  font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-amber-400">
              The Future of
            </span>          
            <br />
            <motion.span
              key={textIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-white inline-block"
            >
              {rotatingTexts[textIndex]}
            </motion.span>
          </h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 text-sm md:text-md text-gray-300 max-w-3xl mx-auto"
          >
            The ultimate platform for luxury fleet management,
            designed for those who demand excellence
          </motion.p>
          
          <motion.div 
            className="mt-10 flex flex-col md:flex-row gap-4 justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <MagneticButton 
              onClick={handleEarlyAccess}
              className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 
                       text-black rounded-full hover:from-amber-500 hover:to-amber-700 
                       transition-all duration-300 font-semibold"
            >
              <Particles />
              Request Early Access
            </MagneticButton>
          </motion.div>

          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-12 inline-flex items-center gap-2 px-4 py-2 rounded-full 
                     border border-amber-500/20 bg-black/50 backdrop-blur-sm"
          >
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-400 text-xs">Trusted by Leading Luxury Brands</span>
          </motion.div>
        </motion.div>

        {/* Side Lines */}
        <div className="absolute inset-y-0 left-8 md:left-12 w-px bg-gradient-to-b from-transparent via-amber-500/20 to-transparent" />
        <div className="absolute inset-y-0 right-8 md:right-12 w-px bg-gradient-to-b from-transparent via-amber-500/20 to-transparent" />
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
        >
          <div className="relative w-full max-w-5xl mx-4">
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="relative pt-[56.25%]">
              <iframe
                src={`${SHOWCASE_VIDEO_URL}?autoplay=1&modestbranding=1&rel=0`}
                className="absolute inset-0 w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Showcase Video"
              />
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}
